import { Component, JSX, Match, Switch, createMemo } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { getSvgIcon, TempSvgIcon } from './svg-icons/get-svg-icon';
import { IconSize } from './icon-size';
import { IconColor } from './icon-color';

export const CONNECT_ICON_FONT_URL =
  'https://resources.connect.trimble.com/1.24.0/fonts/icon-font.min.css';

export interface IconProps {
  icon: string | TempSvgIcon;
  url?: string;
  size?: IconSize;
  color?: IconColor | string;
  customClass?: string;
  /**
   * Forwarded as the `slot` attribute on the root rendered element.
   * Use this to place the icon into a named slot of a parent web component
   * (e.g. slot="start-icon" for modus-wc-menu-item).
   */
  slot?: string;
  /**
   * When true, suppresses the automatic <link> tag injection for the Connect
   * icon font inside the isTcIcon() branch. Use this when the caller already
   * injects the font link once at a higher level (e.g. SideNavigation) to
   * avoid placing a <link> element in a web component's default slot, which
   * would pollute it and cause layout inconsistencies.
   */
  skipFontLink?: boolean;
}

/**
 * This Icon wrapper returns either <i> element that uses the Connect icon set
 * if the `icon` prop is passed in, and the icon name starts with `tc-`. If an
 * icon from the Modus icon set is passed in, it will return a `modus-wc-icon` element.
 * For the cases where the icon is only available on Figma, the svg content can be added to
 * the body of the getSvgIcon function with a key starting with svg-.
 * */
const SvgIconRenderer: Component<{ icon: TempSvgIcon; size: IconSize }> = (
  props
): JSX.Element => {
  const svgContent = createMemo(() => getSvgIcon(props.icon, props.size));
  return <>{svgContent()}</>;
};

const Icon = (props: IconProps): JSX.Element => {
  const getColorStyle = (): JSX.CSSProperties | undefined => {
    switch (props.color) {
      case IconColor.Primary:
        return { color: 'var(--modus-wc-color-primary)' };
      case IconColor.Secondary:
      case IconColor.Default:
        return { color: 'var(--modus-wc-color-trimble-gray, #363545)' };
      case IconColor.Inverse:
        return { color: 'var(--modus-wc-color-white)' };
      case IconColor.Tertiary:
        return { color: 'var(--modus-wc-color-gray-7, #595868)' };
      case undefined:
        return undefined;
      default:
        return { color: props.color };
    }
  };

  function toModusSize(s: IconSize): 'xs' | 'sm' | 'md' | 'lg' {
    switch (s) {
      case 'icon-tiny':
        return 'xs';
      case 'icon-small':
        return 'sm';
      case 'icon-medium':
        return 'md';
      case 'icon-large':
      case 'icon-xlarge': // Modus doesn't support xlarge at the moment.
        return 'lg';
    }
  }

  const iconSizeClass = (): string => {
    switch (props.size) {
      case 'icon-small':
        return 'i16';
      case 'icon-large':
        return 'i32';
      case 'icon-xlarge':
        return 'text-icon-48';
      default:
        return '';
    }
  };

  const imgSizeClass = (): string => {
    switch (props.size) {
      case 'icon-tiny':
        return 'w-3 h-3';
      case 'icon-small':
        return 'w-4 h-4';
      case 'icon-medium':
      case undefined:
        return 'w-5 h-5';
      case 'icon-large':
        return 'w-8 h-8';
      case 'icon-xlarge':
        return 'w-12 h-12';
      default:
        throw new UnreachableCaseError(props.size);
    }
  };

  const isSvgIcon = (): boolean => props.icon.startsWith('svg-');
  const isTcIcon = (): boolean => {
    // some icon props are passed in with "icon-font" and other classes
    // at the start which forces the modus-wc-icon fallback which then fails,
    // so check for tc- and some characters after it
    return /tc-[^\s]+/.test(props.icon);
  };

  return (
    <Switch
      fallback={
        <span slot={props.slot} style={getColorStyle()}>
          <modus-wc-icon
            custom-class={props.customClass}
            name={props.icon}
            size={toModusSize(props.size || 'icon-medium')}
          />
        </span>
      }
    >
      <Match when={props.url}>
        <img
          slot={props.slot}
          src={props.url}
          alt={props.icon}
          class={`${imgSizeClass()} object-contain invert ${props.customClass ?? ''}`}
        />
      </Match>
      <Match when={isTcIcon()}>
        <>
          {!props.skipFontLink && (
            <link rel="stylesheet" href={CONNECT_ICON_FONT_URL} />
          )}
          <span slot={props.slot} style={getColorStyle()}>
            <modus-wc-icon
              custom-class={`icon-font ${props.icon} ${iconSizeClass()} ${props.customClass ?? ''}`}
              name=""
              size={toModusSize(props.size || 'icon-medium')}
            />
          </span>
        </>
      </Match>
      <Match when={isSvgIcon()}>
        <span
          slot={props.slot}
          class={props.customClass}
          style={getColorStyle()}
        >
          <SvgIconRenderer
            icon={props.icon as TempSvgIcon}
            size={props.size || 'icon-medium'}
          />
        </span>
      </Match>
    </Switch>
  );
};

export default Icon;
